import React, { useState } from 'react';
import { Badge, Box, Button, Flex, SimpleGrid, Spinner, Text, useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowClockwise, BookOpen, Pencil, Warning } from 'phosphor-react';

import { ReturnButton } from '../../../components/buttons/ReturnButton';
import { PageHeader } from '../../../components/PageHeader';
import { GlassCard, GlassPageBg } from '../../../components/glass';
import {
  IModeloReferencia,
  TipoOperacaoSocietario,
  useSocietario,
} from '../../../hooks/useSocietario';
import { extractApiErrorMessage } from '../../../utils/ErrorUtils';
import { EditarModeloModal } from './components/EditarModeloModal';

const tipoLabels: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração contratual',
  transformacao: 'Transformação (EI → LTDA)',
};

const tipoDescricoes: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Modelo usado ao abrir uma nova sociedade',
  alteracao: 'Modelo usado em alterações contratuais',
  transformacao: 'Modelo usado na transformação de EI em LTDA',
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

interface ModeloCardProps {
  tipo: TipoOperacaoSocietario;
  modelo: IModeloReferencia | null;
  isFetching: boolean;
  // eslint-disable-next-line no-unused-vars
  onEditar: (tipo: TipoOperacaoSocietario) => void;
}

const ModeloCard = ({ tipo, modelo, isFetching, onEditar }: ModeloCardProps) => {
  const semCorpo = modelo && !(modelo.corpo_referencia ?? '').trim();
  const tamanho = (modelo?.corpo_referencia ?? '').length;
  return (
    <GlassCard
      intensity="strong"
      p={6}
      borderColor={semCorpo ? 'rgba(221, 107, 32, 0.45)' : undefined}
    >
      <Flex align="center" gap={3} mb={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          p={2}
          bg={tipo === 'constituicao' ? 'blue.50' : 'orange.50'}
        >
          <BookOpen
            size={24}
            color={tipo === 'constituicao' ? '#1976D2' : '#F2994A'}
            weight="duotone"
          />
        </Box>
        <Box>
          <Text fontWeight="bold" fontSize="md">
            {tipoLabels[tipo]}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {tipoDescricoes[tipo]}
          </Text>
        </Box>
      </Flex>

      {/* eslint-disable-next-line no-nested-ternary */}
      {isFetching ? (
        <Flex align="center" gap={2} py={4}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">
            Carregando…
          </Text>
        </Flex>
      ) : modelo ? (
        <Box bg="gray.50" borderRadius="md" p={3} mb={3}>
          <Flex justify="space-between" align="center" mb={1}>
            <Text fontWeight="medium" fontSize="sm">
              {modelo.nome}
            </Text>
            <Badge colorScheme={semCorpo ? 'orange' : 'green'}>
              {semCorpo ? 'Sem corpo' : 'Ativo'}
            </Badge>
          </Flex>
          <Text fontSize="xs" color="gray.600">
            Versão: <strong>{modelo.versao}</strong> · Última revisão:{' '}
            {formatDate(modelo.dt_ultima_revisao ?? modelo.dt_atualizacao)}
          </Text>
          <Text fontSize="xs" color="gray.600">
            Tamanho do corpo: <strong>{tamanho.toLocaleString('pt-BR')} caracteres</strong>
          </Text>
          {modelo.empresa_id ? (
            <Text fontSize="xs" color="purple.600" mt={1}>
              Específico da empresa #{modelo.empresa_id}
            </Text>
          ) : (
            <Text fontSize="xs" color="gray.500" mt={1}>
              Modelo global
            </Text>
          )}
          {modelo.descricao && (
            <Text fontSize="xs" color="gray.600" mt={1} noOfLines={2}>
              {modelo.descricao}
            </Text>
          )}
        </Box>
      ) : (
        <Box bg="orange.50" borderRadius="md" p={3} mb={3}>
          <Flex align="center" gap={2}>
            <Warning size={18} color="#DD6B20" weight="duotone" />
            <Text fontSize="sm" color="orange.700" fontWeight="medium">
              Nenhum modelo cadastrado
            </Text>
          </Flex>
          <Text fontSize="xs" color="orange.600" mt={1}>
            Cadastre um modelo antes de gerar contratos via engine v2.
          </Text>
        </Box>
      )}

      <Button
        w="full"
        colorScheme="blue"
        leftIcon={modelo ? <Pencil size={16} /> : <ArrowClockwise size={16} />}
        onClick={() => onEditar(tipo)}
      >
        {modelo ? 'Editar / nova versão' : 'Cadastrar modelo'}
      </Button>
    </GlassCard>
  );
};

export const SocietarioModelosPage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { getModeloAtivo, criarModeloReferencia } = useSocietario();
  const [tipoEditando, setTipoEditando] = useState<TipoOperacaoSocietario | null>(null);

  const constituicaoQuery = useQuery({
    queryKey: ['societario-modelo-ativo', 'constituicao'],
    queryFn: () => getModeloAtivo('constituicao'),
  });
  const alteracaoQuery = useQuery({
    queryKey: ['societario-modelo-ativo', 'alteracao'],
    queryFn: () => getModeloAtivo('alteracao'),
  });

  const criarMutation = useMutation({ mutationFn: criarModeloReferencia });

  const handleSalvar = async (data: {
    nome: string;
    corpo_referencia: string;
    instrucoes_globais: string | null;
    descricao: string | null;
  }) => {
    if (!tipoEditando) return;
    try {
      await criarMutation.mutateAsync({
        tipo_operacao: tipoEditando,
        nome: data.nome,
        corpo_referencia: data.corpo_referencia,
        instrucoes_globais: data.instrucoes_globais,
        descricao: data.descricao,
      });
      await queryClient.invalidateQueries({ queryKey: ['societario-modelo-ativo'] });
      toast({ status: 'success', title: 'Nova versão do modelo salva' });
      setTipoEditando(null);
    } catch (err) {
      toast({
        status: 'error',
        title: 'Falha ao salvar modelo',
        description: extractApiErrorMessage(err),
      });
    }
  };

  const modeloEditado =
    tipoEditando === 'constituicao' ? constituicaoQuery.data : alteracaoQuery.data;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" px={{ base: 4, md: 6 }} py={6}>
        <ReturnButton previousPage="societario" label="Voltar" />
        <PageHeader
          title="Modelos de referência"
          subtitle="Modelos em markdown editáveis usados como referência pela IA na geração de contratos. Cada save cria uma nova versão e desativa a anterior."
        />

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mt={4}>
          <ModeloCard
            tipo="constituicao"
            modelo={constituicaoQuery.data ?? null}
            isFetching={constituicaoQuery.isFetching}
            onEditar={setTipoEditando}
          />
          <ModeloCard
            tipo="alteracao"
            modelo={alteracaoQuery.data ?? null}
            isFetching={alteracaoQuery.isFetching}
            onEditar={setTipoEditando}
          />
        </SimpleGrid>

        {tipoEditando && (
          <EditarModeloModal
            isOpen={!!tipoEditando}
            onClose={() => setTipoEditando(null)}
            tipoOperacao={tipoEditando}
            modeloAtual={modeloEditado ?? null}
            onSubmit={handleSalvar}
            isLoading={criarMutation.isPending}
          />
        )}
      </Box>
    </GlassPageBg>
  );
};

export default SocietarioModelosPage;
